import type { FC } from 'react';
import { useEffect, useMemo } from 'react';

import { Outlet, useNavigate } from 'react-router';

import AppTopBar from '@/app/components/AppTopBar';
import { ExpiredBanner } from '@/app/components/ExpiredBanner';
import { ImpersonatorBanner } from '@/app/components/ImpersonatorBanner';
import { NavigationMenu } from '@/app/components/NavigationMenu';
import { VersionUpdateBanner } from '@/app/components/VersionUpdateBanner';
import { Permission } from '@/entities/permissions';
import UpgradeToPremiumButton from '@/features/_shared/UpgradeToPremium/UpgradeToPremiumButton';
import { usePermission, useUserDetails } from '@/features/_shared/user';
import { useSavedFiltersSync } from '@/features/catalog/api/savedFilters';
import { useDashboardSync } from '@/features/dashboard/hooks';
import ExportChartButton from '@/features/data-hub/components/ExportChartButton';
import { ExplainerVideoModal, useExplainerVideoModal } from '@/features/explainer-video';
import FactsheetDialog from '@/features/factsheet/components/FactsheetDialog';
import { ExportChartButtonContext } from '@/features/factsheet/state/ExportChartButtonContext';
import type { ChartButtonRenderer } from '@/features/factsheet/state/ExportChartButtonContext';
import { FactsheetOpenStateProvider } from '@/features/factsheet/state/FactsheetOpenStateProvider';
import { FactsheetOverlayContainerProvider } from '@/features/factsheet/state/FactsheetOverlayContainerProvider';
import type { FactsheetSelectionState } from '@/features/factsheet/state/FactsheetSelectionContext';
import { useExpiredBannerContent } from '@/features/profile/hooks/useExpiredBannerContent';
import { useSelectionFunctions } from '@/features/symbol-selector';
import { PageTitleProvider } from '@/shared/pageTitle';
import { useAppSelector } from '@/store/hooks';

const renderExportChartButton: ChartButtonRenderer = (graphType, sx, iconSize) => (
  <ExportChartButton
    graphType={graphType}
    iconSize={iconSize}
    sx={sx}
  />
);

const shouldShowUpgradeButton = (
  isUserExpired: boolean | undefined,
  hasPremiumWidgetPermission: boolean,
  hasExpiredBannerContent: boolean
) => isUserExpired !== undefined && !hasPremiumWidgetPermission && !hasExpiredBannerContent;

const useAppLayoutRedirects = (impersonatorEmail: string | null | undefined) => {
  const navigate = useNavigate();
  const tripTokenState = useAppSelector(state => state.tripToken);

  useEffect(() => {
    // When impersonation prevents T&C acceptance, stay on the requested route.
    if (impersonatorEmail) {
      return;
    }
    if (tripTokenState.status === 'fulfilled' && !tripTokenState.terms) {
      void navigate('/welcome');
    }
  }, [tripTokenState.status, tripTokenState.terms, impersonatorEmail, navigate]);
};

/**
 * Main application layout with navigation, banners, and content area.
 *
 * Features:
 * - Persistent navigation menu (sidebar)
 * - Version update banner
 * - Expired subscription banner
 * - Upgrade to premium button (for basic users)
 * - Factsheet dialog handling
 * - Terms acceptance check and redirect
 * - Responsive container with max-width constraints
 *
 * This layout wraps all authenticated application pages. The Ask-GX assistant
 * is not mounted here — it is only available as the desktop-docked panel
 * rendered by `AskGXDockedLayout` on `/` and `/morning-intel`.
 */
export const AppLayout: FC = () => {
  useDashboardSync();
  useSavedFiltersSync();
  const { isUserExpired, impersonatorEmail } = useUserDetails();
  const { hasPermission } = usePermission();
  const expiredBannerContent = useExpiredBannerContent();
  const showUpgradeButton = useMemo(
    () =>
      shouldShowUpgradeButton(
        isUserExpired,
        hasPermission(Permission.DashboardPremiumWidgets),
        Boolean(expiredBannerContent)
      ),
    [expiredBannerContent, hasPermission, isUserExpired]
  );

  const { isOpen: isExplainerVideoOpen, onDismiss: onExplainerVideoDismiss } =
    useExplainerVideoModal();

  useAppLayoutRedirects(impersonatorEmail);

  const {
    isSymbolSelected,
    toggleSymbolInSelection,
    addSymbolsToSelection,
    removeSymbolsFromSelection,
    areAllSymbolsSelected,
    isSpotLocked
  } = useSelectionFunctions();
  const factsheetSelectionState: FactsheetSelectionState = {
    isSymbolSelected,
    toggleSymbolInSelection,
    addSymbolsToSelection,
    removeSymbolsFromSelection,
    areAllSymbolsSelected,
    isSpotLocked
  };

  return (
    <div
      data-slot='app-layout'
      className='mx-auto flex h-screen w-full max-w-[2000px] overflow-hidden xl:border-l xl:border-gx-tile-light'
    >
      <FactsheetOverlayContainerProvider>
        <FactsheetOpenStateProvider>
          <ExportChartButtonContext value={renderExportChartButton}>
            <FactsheetDialog selectionState={factsheetSelectionState} />
          </ExportChartButtonContext>
          <ExplainerVideoModal
            open={isExplainerVideoOpen}
            onClose={onExplainerVideoDismiss}
          />
          <NavigationMenu>
            <PageTitleProvider>
              <div className='relative box-border flex h-screen w-full min-w-0 flex-col'>
                <VersionUpdateBanner />
                <ExpiredBanner />
                <AppTopBar />
                <div className='flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto'>
                  <ImpersonatorBanner />
                  {showUpgradeButton && (
                    <div className='flex justify-end gap-6'>
                      <UpgradeToPremiumButton
                        size='large'
                        sx={{ flexShrink: 0, whiteSpace: 'nowrap', fontSize: 16 }}
                        type='upgrade'
                      />
                    </div>
                  )}
                  <Outlet />
                </div>
              </div>
            </PageTitleProvider>
          </NavigationMenu>
        </FactsheetOpenStateProvider>
      </FactsheetOverlayContainerProvider>
    </div>
  );
};
